import { ItemRead, ProgressionProgramRead, ResourceState } from '@/api/dashboard'
import { TableCell, TableRow } from '@/ui'
import {
  Button,
  DotsHorizontal,
  Edit03,
  Menu,
  MenuItem,
  ModalPresetConfirm,
  Popper,
  Trash,
  useModal,
} from '@dashboard/ui'
import { useProgressionProgramDeleteMutate } from '@/layouts/progression-program/api'
import { PROGRESSIVE_PROGRAM_PATH_FORM, useGenerateCompanyPath } from '@/libs'
import { useCurrentUser } from '@/api/useCurrentUser'
import { TimeLimitInfo } from '@/components'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { DEFAULT_LOAD_LIMIT, useGameItemsQuery } from '@/api/useGameItems'
import { NestedItems } from '@/layouts/components/NestedItems'
import { SkuImageName } from '@/layouts/game-items'

export const ProgressionTableRow = ({ program }: { program: ProgressionProgramRead }) => {
  const { companyId, gameId } = useGenerateCompanyPath()
  const { canEdit } = useCurrentUser()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { generatePath } = useGenerateCompanyPath()
  const { mutateAsync: deleteMutateAsync } = useProgressionProgramDeleteMutate(companyId, gameId)

  const { data: items = [] } = useGameItemsQuery(companyId, gameId, {
    limit: DEFAULT_LOAD_LIMIT,
    state: ResourceState.Active,
    ids: program.pp_granting_item_ids.join(','),
  })

  const openConfirmDelete = useModal<{ item: ProgressionProgramRead }>(props => (
    <ModalPresetConfirm
      {...props}
      title={{
        children: t('confirm.title'),
        subtitle: t('confirm.text', { name: props.item.name }),
      }}
      cancel={{
        label: t('confirm.no'),
      }}
      submit={{
        color: 'danger',
        callback: async () => {
          props.onClose()
          deleteMutateAsync({ id: props.item.id })
        },
        label: t('remove'),
      }}
    />
  ))

  const openEditForm = (id: string) => {
    navigate(generatePath(PROGRESSIVE_PROGRAM_PATH_FORM, { programId: id }, true))
  }

  return (
    <TableRow key={program.id} to={generatePath(PROGRESSIVE_PROGRAM_PATH_FORM, { programId: program.id }, true)}>
      <TableCell width="100%">{program.name}</TableCell>
      <TableCell width={200}>
        {items.length == 1 ? <SkuImageName item={items[0]} /> : <NestedItems items={(items || []) as ItemRead[]} />}
      </TableCell>

      <TableCell width={160}>
        <TimeLimitInfo value={program.start_at} />
      </TableCell>
      <TableCell width={160}>
        <TimeLimitInfo value={program.end_at} />
      </TableCell>

      <TableCell width={80}>
        {canEdit && (
          <div
            className="text-right"
            onClick={e => {
              e.stopPropagation()
              e.preventDefault()
            }}
          >
            <Popper
              popper={props => (
                <Menu {...props}>
                  <MenuItem
                    icon={Edit03}
                    onClick={() => {
                      openEditForm(program.id)
                      props.onClose()
                    }}
                  >
                    {t('edit')}
                  </MenuItem>
                  <MenuItem
                    icon={Trash}
                    variant="danger"
                    onClick={() => {
                      openConfirmDelete({
                        item: program,
                      })
                      props.onClose()
                    }}
                  >
                    {t('delete')}
                  </MenuItem>
                </Menu>
              )}
            >
              <Button variant="tertiary" color="secondary">
                <DotsHorizontal />
              </Button>
            </Popper>
          </div>
        )}
      </TableCell>
    </TableRow>
  )
}
